import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// Support address as string or object { street, city, state, zipCode, country }
const getAddressStr = (c) => {
  const a = c.address;
  if (a == null) return '';
  if (typeof a === 'string') return a;
  const parts = [a.street, a.city, a.state, a.zipCode].filter(Boolean);
  return parts.join(', ');
};
const getCity = (c) => (typeof c.address === 'object' && c.address?.city) || c.city || '';
const getState = (c) => (typeof c.address === 'object' && c.address?.state) || c.state || '';
const getZipCode = (c) => (typeof c.address === 'object' && c.address?.zipCode) || c.zipCode || '';

const CustomerOverview = ({ customers = [], compact = false }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.trim().toLowerCase();
    return customers.filter(
      (c) =>
        (c.fullName && c.fullName.toLowerCase().includes(q)) ||
        (c.firstName && c.firstName.toLowerCase().includes(q)) ||
        (c.lastName && c.lastName.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && String(c.phone).toLowerCase().includes(q)) ||
        getAddressStr(c).toLowerCase().includes(q) ||
        getCity(c).toLowerCase().includes(q) ||
        getState(c).toLowerCase().includes(q) ||
        String(getZipCode(c)).includes(q)
    );
  }, [customers, search]);

  const byState = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      const s = getState(c).trim() || '—';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [customers]);

  const byCity = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      const city = getCity(c).trim() || '—';
      map[city] = (map[city] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [customers]);

  const totalOpenBalance = useMemo(
    () => customers.reduce((sum, c) => sum + (Number(c.openBalance) || 0), 0),
    [customers]
  );

  const formatCurrency = (n) =>
    typeof n === 'number' && !Number.isNaN(n)
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
      : '—';

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-secondary-500">Total customers</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">{customers.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-secondary-500">Showing</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">
            {filtered.length}
            {search && ` of ${customers.length}`}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-secondary-500">Open balance (total)</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">{formatCurrency(totalOpenBalance)}</p>
        </div>
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-secondary-500">Top state</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">
            {byState[0] ? `${byState[0][0]} (${byState[0][1]})` : '—'}
          </p>
        </div>
      </div>

      {/* By state / city */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <h3 className="font-semibold text-secondary-800 mb-3">By state</h3>
          <ul className="space-y-1.5 text-sm">
            {byState.slice(0, 8).map(([state, count]) => (
              <li key={state} className="flex justify-between">
                <span className="text-secondary-700">{state}</span>
                <span className="font-medium text-secondary-900">{count}</span>
              </li>
            ))}
            {byState.length === 0 && <li className="text-secondary-500">No data</li>}
          </ul>
        </div>
        <div className="bg-white rounded-xl border border-secondary-200 p-4 shadow-sm">
          <h3 className="font-semibold text-secondary-800 mb-3">By city (top 10)</h3>
          <ul className="space-y-1.5 text-sm">
            {byCity.map(([city, count]) => (
              <li key={city} className="flex justify-between">
                <span className="text-secondary-700">{city}</span>
                <span className="font-medium text-secondary-900">{count}</span>
              </li>
            ))}
            {byCity.length === 0 && <li className="text-secondary-500">No data</li>}
          </ul>
        </div>
      </div>

      {/* Customer list (hidden when compact = true, e.g. when used inside Customers tab with Management below) */}
      {!compact && (
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-secondary-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="font-semibold text-secondary-800">Customer list</h3>
            <div className="flex flex-col xs:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search name, email, phone, city, state..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input flex-1 min-w-0 min-h-[44px] touch-manipulation"
              />
              <Link
                to="/customers"
                className="btn-primary whitespace-nowrap min-h-[44px] flex items-center justify-center touch-manipulation"
              >
                Manage & upload Excel
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">City</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Zip</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-600 uppercase tracking-wider">Open balance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-secondary-500">
                      {customers.length === 0
                        ? 'No customers yet. Upload your Excel file below.'
                        : 'No customers match your search.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-secondary-50/50">
                      <td className="px-4 py-3 text-sm font-medium text-secondary-900 whitespace-nowrap">
                        {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-700 max-w-[200px] truncate" title={getAddressStr(c)}>
                        {getAddressStr(c) || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-700">{getCity(c) || '—'}</td>
                      <td className="px-4 py-3 text-sm text-secondary-700">{getState(c) || '—'}</td>
                      <td className="px-4 py-3 text-sm text-secondary-700">{getZipCode(c) || '—'}</td>
                      <td className="px-4 py-3 text-sm text-secondary-700">{c.phone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-secondary-700 truncate max-w-[180px]" title={c.email}>
                        {c.email || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-700 text-right font-medium">
                        {(c.openBalance != null && Number(c.openBalance) !== 0)
                          ? formatCurrency(Number(c.openBalance))
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOverview;
